import React, { useEffect } from 'react';
import { Mail, Newspaper, Gift } from 'lucide-react';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHeader } from '@/components/layout/PageHeader';
import { analytics } from '@/analytics';

// Custom logo components
const PatreonLogo = ({ className = 'w-6 h-6' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M0 .48v23.04h4.22V.48zm15.385 0c-4.764 0-8.641 3.88-8.641 8.65 0 4.755 3.877 8.623 8.641 8.623 4.75 0 8.615-3.868 8.615-8.623C24 4.36 20.136.48 15.385.48z" />
  </svg>
);

const DiscordLogo = ({ className = 'w-6 h-6' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

// Social Media Icons
const socialMediaPlatforms = [
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@backyahdbbq',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    hoverBg: 'hover:bg-red-100',
    hoverBorder: 'hover:border-red-400',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@backyahdbbq',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    hoverBg: 'hover:bg-gray-100',
    hoverBorder: 'hover:border-gray-500',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/backyahdbbq/',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    hoverBg: 'hover:bg-pink-100',
    hoverBorder: 'hover:border-pink-400',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/backyahdbbq',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    hoverBg: 'hover:bg-slate-200',
    hoverBorder: 'hover:border-slate-700',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: 'X',
    url: 'https://x.com/BackyahdBBQ',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    hoverBg: 'hover:bg-sky-100',
    hoverBorder: 'hover:border-sky-400',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: 'Threads',
    url: 'https://www.threads.net/@backyahdbbq',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    hoverBg: 'hover:bg-gray-100',
    hoverBorder: 'hover:border-gray-500',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 013.02.142l-.126 1.974c-.805-.097-1.639-.141-2.475-.125-1.016.044-1.838.33-2.377.825-.475.437-.72.978-.69 1.522.025.458.237.86.596 1.134.429.328 1.084.484 1.823.433 1.052-.058 1.88-.485 2.46-1.27.488-.66.792-1.55.903-2.653l.014-.116c-.014-1.212-.261-2.152-.735-2.797-.51-.697-1.372-1.062-2.563-1.086-.935-.015-1.719.145-2.334.477-.615.332-1.04.802-1.265 1.397l-1.967-.597c.355-1.17 1.042-2.13 2.042-2.855.998-.725 2.269-1.113 3.777-1.151 1.797.045 3.192.649 4.146 1.794.953 1.143 1.437 2.748 1.437 4.77v.072c.046.92.317 1.67.804 2.226.51.583 1.246.88 2.187.88.574 0 1.106-.116 1.583-.349v2.107c-.666.277-1.37.416-2.092.416-1.434 0-2.586-.559-3.427-1.66-.666.838-1.494 1.474-2.465 1.891-1.023.44-2.173.66-3.418.66z" />
      </svg>
    ),
  },
  {
    name: 'Rumble',
    url: 'https://rumble.com/user/backyahdbbq',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    hoverBg: 'hover:bg-green-100',
    hoverBorder: 'hover:border-green-400',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16.5 3h-9C5.015 3 3 5.015 3 7.5v9C3 18.985 5.015 21 7.5 21h9c2.485 0 4.5-2.015 4.5-4.5v-9C21 5.015 18.985 3 16.5 3zm-6.75 12.375L6 12l3.75-3.375v6.75zm6-1.5l-3.75 3.375v-6.75L15.75 13.875z" />
      </svg>
    ),
  },
  {
    name: 'Patreon',
    url: 'https://www.patreon.com/backyahdbbq',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    hoverBg: 'hover:bg-slate-200',
    hoverBorder: 'hover:border-slate-700',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M15.386.524c-4.764 0-8.64 3.876-8.64 8.64 0 4.75 3.876 8.613 8.64 8.613 4.75 0 8.614-3.864 8.614-8.613C24 4.4 20.136.524 15.386.524M.003 23.537h4.22V.524H.003" />
      </svg>
    ),
  },
];

export const ContactPage = () => {
  useEffect(() => {
    analytics.trackContactView();
  }, []);

  const handleSocialClick = (platform: string, url: string) => {
    analytics.trackSocialLinkClick(platform, url);
  };

  const handleEmailClick = () => {
    analytics.trackEmailClick('contact_email');
  };

  return (
    <>
      <SEOHead
        title="Contact - Connect With Our BBQ Community"
        description="Follow Backyahd BBQ on YouTube, Instagram, Facebook, TikTok, Patreon and Discord. Get BBQ tips, recipes, gear reviews, and join our community of smoking enthusiasts."
        canonicalUrl="/contact"
        keywords={[
          'contact backyahdbbq',
          'bbq community',
          'bbq social media',
          'youtube bbq',
          'instagram bbq',
          'discord bbq',
        ]}
      />

      <PageHeader title="Contact" subtitle="Connect with our BBQ community" />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Contact */}
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Get in Touch</h2>
            </div>
            <p className="text-gray-600 mb-3">
              Please email for sponsorship, integration, and collaboration opportunities:
            </p>
            <a
              href="mailto:jonny@backyahdbbq.com"
              onClick={handleEmailClick}
              className="text-lg font-medium text-slate-800 hover:text-slate-800 hover:underline"
            >
              jonny@backyahdbbq.com
            </a>
          </div>
        </div>

        {/* Community Sections */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Patreon */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <PatreonLogo className="w-5 h-5" />
              <h2 className="text-xl font-semibold text-gray-900">Patreon</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Join our Patreon to get access to our private Discord chat community. Our primary
              focus is to help you with your cooks one on one and within the private community.
            </p>
            <a
              href="https://www.patreon.com/backyahdbbq"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSocialClick('Patreon', 'https://www.patreon.com/backyahdbbq')}
              className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-900 font-medium rounded transition-colors"
              style={{ color: 'white' }}
            >
              Join Patreon →
            </a>
          </div>

          {/* Discord */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <DiscordLogo className="w-5 h-5" />
              <h2 className="text-xl font-semibold text-gray-900">Discord</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Join our Discord community to chat with fellow BBQ enthusiasts, get cooking tips, and
              share your smoking adventures.
            </p>
            <a
              href="https://discord.gg/usznVtntGC"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSocialClick('Discord', 'https://discord.gg/usznVtntGC')}
              className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 font-medium rounded transition-colors"
              style={{ color: 'white' }}
            >
              Join Discord →
            </a>
          </div>
        </div>

        {/* Newsletter */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Newspaper className="w-5 h-5 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Newsletter</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Subscribe to our newsletter to get regular updates on what we&apos;re working on:
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4 flex justify-center">
            <iframe
              src="https://backyahdbbq.substack.com/embed"
              width="480"
              height="320"
              style={{ border: '1px solid #EEE', background: 'white' }}
              frameBorder="0"
              scrolling="no"
              title="Backyahd BBQ Newsletter Signup"
              className="max-w-full"
            />
          </div>
        </div>

        {/* Discount Codes */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-5 h-5 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Discount Codes</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Check out our exclusive discount codes for BBQ gear and accessories:
          </p>
          <a
            href="/codes"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-900 font-medium rounded transition-colors"
            style={{ color: 'white' }}
          >
            View Discount Codes →
          </a>
        </div>

        {/* Social Media */}
        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Follow Us</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {socialMediaPlatforms.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick(social.name, social.url)}
                className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg p-4 text-center transition-colors flex flex-col items-center gap-3 group hover:shadow-sm"
              >
                <div className="group-hover:scale-105 transition-transform motion-reduce:transition-none motion-reduce:group-hover:scale-100">
                  {social.icon}
                </div>
                <span className="text-sm font-medium text-gray-700">{social.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
